import { Box, Button, Stack } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useRef, useState } from 'react';
import { useFormik } from 'formik';
import { number, object } from 'yup';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import AutoWidthInput from '../../form/base/AutoWidthInput';
import {
  cleanCents,
  formatDollarAmount,
  important,
} from '../../../utils/global/helper';
import { InitiatePayoutResponse, StripeSummary } from '../../../types/stripe';
import useStripeSummary from '../../../hooks/stripe/useStripeSummary';
import useInitiatePayout from '../../../hooks/stripe/useInitiatePayout';
import {
  INSTANT_PAYOUT_DAILY_EXHAUSTED_TITLE,
  INSTANT_PAYOUT_LIMITED_SUBTITLE,
  INSTANT_PAYOUT_LIMITED_TITLE,
} from '../../../helpers/payments';
import ColoredAlert from '../../global/ColoredAlert';
import DetailRow from './DetailRow';
import PercentButtons from './PercentButtons';
import useConfirm from '../../../hooks/useConfirm';

interface Props {
  summary: StripeSummary;
  onSuccess: (data: InitiatePayoutResponse) => void;
}

function PayoutForm({ summary, onSuccess }: Props) {
  const confirm = useConfirm();
  const { isFetching, refetch } = useStripeSummary();
  const { mutateAsync: initiatePayout } = useInitiatePayout();

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const isNoPayoutAvailable = summary.maximumAmount < summary.minimumAmount;
  const isTierLimitingInstant =
    summary.isTierLimited && summary.balance.instant > summary.maximumAmount;
  const [hasFailed, setHasFailed] = useState(isNoPayoutAvailable);
  const showLimitedAlert = hasFailed || isTierLimitingInstant;

  const effectiveInstantCents = Math.min(
    summary.balance.instant,
    summary.maximumAmount,
  );

  const formik = useFormik({
    initialValues: {
      amount: (effectiveInstantCents / 100).toFixed(2),
    },
    validationSchema: object({
      amount: number()
        .transform(value => parseFloat(value))
        .min(0, 'Amount must be greater than 0')
        .required('Amount is required'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!summary) throw new Error('Summary not found');

        setSubmitting(true);

        if (summary.isAccountUnderTwoDaysOld) {
          setHasFailed(true);
          return;
        }

        const cents = cleanCents(parseFloat(values.amount) * 100);

        const isAboveDailyMax = cents > summary.maximumAmount;
        const isAbovePerTxMax = cents > summary.perTransactionMax;
        const isBelowMin = cents < summary.minimumAmount;
        const isDailyLimitedBelowPerTx =
          summary.maximumAmount < summary.perTransactionMax;
        if (isAboveDailyMax && isDailyLimitedBelowPerTx) {
          await confirm({
            title: INSTANT_PAYOUT_LIMITED_TITLE,
            message: INSTANT_PAYOUT_LIMITED_SUBTITLE,
            negativeText: '',
            positiveText: 'I understand',
          });
          formik.setFieldValue(
            'amount',
            (summary.maximumAmount / 100).toFixed(2),
          );
          return;
        }
        if (isAbovePerTxMax) {
          const formattedAmount = formatDollarAmount(
            summary.perTransactionMax / 100,
            true,
          );
          await confirm({
            title: 'Max. Payout',
            message: `You can only payout up to ${formattedAmount} at once. If you need to payout more, please do so in multiple transactions.`,
            negativeText: '',
            positiveText: 'I understand',
          });
          formik.setFieldValue(
            'amount',
            (summary.perTransactionMax / 100).toFixed(2),
          );
          return;
        }
        if (isBelowMin) {
          const formattedAmount = formatDollarAmount(
            summary.minimumAmount / 100,
            true,
          );
          await confirm({
            title: 'Min. Payout',
            message: `The minimum payout amount is ${formattedAmount}.`,
            negativeText: '',
            positiveText: 'I understand',
          });
          formik.setFieldValue(
            'amount',
            (summary.minimumAmount / 100).toFixed(2),
          );
          return;
        }

        const result = await initiatePayout(cents);
        if (!result) throw new Error('Failed to initiate payout');

        await refetch();

        onSuccess(result);
      } catch {
        toast.error('Failed to initiate payout');
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const availableBalanceCents = effectiveInstantCents;
  const availableBalance = availableBalanceCents / 100;

  const payoutAmountCents = cleanCents(
    parseFloat(formik.values.amount || '0') * 100,
  );
  const feeCents = payoutAmountCents * (summary?.feeRate || 0);
  const netAmountCents = payoutAmountCents - feeCents;

  const onBlur = () => {
    formik.setTouched({ amount: true });
  };

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const cleanText = e.target.value.replace(/[^0-9.]/g, '');

    const parts = cleanText.split('.');
    if (parts.length > 2) {
      return;
    }

    if (parts[1] && parts[1].length > 2) {
      return;
    }

    formik.setFieldValue('amount', cleanText);
  };

  const onFocusInput = () => {
    inputRef.current?.select();
  };

  const isTransferDisabled =
    !formik.values.amount ||
    !summary?.instantEligibleExternalAccount ||
    parseFloat(formik.values.amount) <= 0 ||
    parseFloat(formik.values.amount) > availableBalance ||
    payoutAmountCents - feeCents <= 1 ||
    isFetching ||
    isNoPayoutAvailable;

  return (
    <form
      onSubmit={formik.handleSubmit}
      id="instant-payout"
      style={{ width: '100%', padding: 0 }}>
      <Stack gap={2} p={2.5} width="100%" boxSizing="border-box">
        {showLimitedAlert && (
          <ColoredAlert
            animate={false}
            type="warning"
            dismissible={false}
            title={
              isNoPayoutAvailable
                ? INSTANT_PAYOUT_DAILY_EXHAUSTED_TITLE
                : INSTANT_PAYOUT_LIMITED_TITLE
            }
            subtitle={INSTANT_PAYOUT_LIMITED_SUBTITLE}
          />
        )}
        <Stack
          width="100%"
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="center"
          sx={{
            '.MuiInputBase-root': {
              height: important('auto'),
              border: 'none',
            },
            '.MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}>
          <Box position="relative">
            <AutoWidthInput
              id="amount"
              ref={inputRef}
              extraWidth={2}
              value={`$${formik.values.amount}`}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="$0.00"
              placeholderIsMinWidth
            />

            <Button
              variant="text"
              onClick={onFocusInput}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '5px',
                position: 'absolute',
                right: -30,
                top: -2,
                height: '100%',
                aspectRatio: 1,
                minWidth: 0,
              }}>
              <EditOutlinedIcon
                sx={{
                  fontSize: '14px',
                  color: 'text.primary',
                }}
              />
            </Button>
          </Box>
        </Stack>

        <PercentButtons
          availableBalanceCents={availableBalanceCents}
          payoutAmountCents={payoutAmountCents}
          onSelect={amount =>
            formik.setFieldValue('amount', (amount / 100).toFixed(2))
          }
        />

        <Stack width="100%" boxSizing="border-box">
          <DetailRow label="Fee" value={formatDollarAmount(feeCents / 100)} />

          <DetailRow
            label="Transfer To"
            value={`${summary.externalAccounts[0]?.name || 'Bank Account'} ** ${
              summary.externalAccounts[0]?.last4 || '0000'
            }`}
          />
        </Stack>

        <LoadingButton
          type="submit"
          form="instant-payout"
          loading={formik.isSubmitting}
          variant="base"
          color="primary"
          disabled={isTransferDisabled}
          fullWidth>
          Transfer {formatDollarAmount(netAmountCents / 100, true)}
        </LoadingButton>
      </Stack>
    </form>
  );
}

export default PayoutForm;
